import {DrawFrmApp} from "../../drawfrm/DrawFrmApp";
import {FrmSceneLayout} from "../../drawfrm/FrmSceneLayout";
import {ManagedScene2} from "../ManagedScene2";
import {GlassScheduleRow} from "./GlassSchedule";
import {CutPlanStick, RenderAnchorSection} from "../../../../../../api/ElevationTool";

// desired dimension-line width, in export-canvas pixels. Dimension stroke width
// is set in scene units and the whole drawing is shrunk to fit the export sheet,
// which would drive a thin line below a pixel and drop it in the JPEG re-encode.
// The drawing is scaled by minAppliedScale(), so a scene-unit width of
// EXPORT_DIM_TARGET_PX / minAppliedScale() lands at this many pixels on the sheet.
const EXPORT_DIM_TARGET_PX = 0.5;

// swap the app into the export look — plain white, no grid paper, dimensions in
// black — and return a restore fn that puts back the previous look. The drawing
// (lines, fills) keeps its normal colors; only dimensions are forced black so they
// read cleanly on a submittal. Applied on setup and restored on dispose so leaving
// the view returns the editor to its blue/grid styling.
function applyExportStyle(app: DrawFrmApp): () => void {
    const bg = app.getBackground();
    const grid = app.getGrid();
    const dim = {...app.dimensionStyle};

    app.setBackground(0xffffff);
    app.setGrid(null);
    app.dimensionStyle = {
        ...app.dimensionStyle,
        defaultLineColor: 0x5e5e5e,
        defaultLabelColor: 0x000000,
        userColor: 0x000000,
        // the sheet is rendered at a raised resolution and the stroke is sized
        // below to match, so dimension lines are stroked in scene units here
        // rather than pinned to one device pixel.
        nativeLines: false,
    };

    return () => {
        app.setBackground(bg);
        app.setGrid(grid);
        app.dimensionStyle = dim;
    };
}

// base for the submittal export scenes: forces the export look, lays out the
// drawing, and restores the editor look on teardown. The elevation name/label is
// not drawn on the image — it is printed as a footer under each tile on the
// submittal grid (see openings/ImageTilePage.tsx). Subclasses supply the drawing
// content.
export abstract class ExportScene extends ManagedScene2 {
    private restoreStyle: (() => void) | null = null;
    private restoreViewSettings: (() => void) | null = null;

    // the lites this export measured, captured on the last layout pass. The headless
    // render reports them so the glazing submittal can list the panels this variant
    // needs — the cut-outs come out of the layout solver, so there is nowhere else to
    // read them from. Empty until setup() has run.
    private measuredGlass: GlassScheduleRow[] = [];

    // the frame cut plan this export measured, captured on the last layout pass like measuredGlass.
    private measuredCutPlan: CutPlanStick[] = [];

    // the frame anchor sections this export measured, captured on the last layout pass like
    // measuredGlass: an auto qty comes out of the layout solver.
    private measuredAnchors: RenderAnchorSection[] = [];

    // the drawing to export (already added to the scene via this.add), returned as
    // a layout node that fills the export area.
    protected abstract buildContent(): FrmSceneLayout;

    // whether to force the "Show All Dimensions" toggle on while this view is up —
    // e.g. the screen export shows every stick's thickness. Snapshotted and
    // restored around the view. Defaults to off.
    protected showAllDimensions(): boolean {
        return false;
    }

    // whether to hide hardware crosshair labels while this view is up (the screen
    // export shows markers without their product codes). Snapshotted and restored
    // around the view. Defaults to off.
    protected hideHardwareLabels(): boolean {
        return false;
    }

    // whether to draw the frame dimensions stacked outside the drawing (the screen
    // export). Snapshotted and restored around the view. Defaults to off.
    protected externalDimensions(): boolean {
        return false;
    }

    // whether to restrict the external dimensions to overall + door-cell + hardware +
    // hinges (the hardware export). Only meaningful with externalDimensions() on.
    // Snapshotted and restored around the view. Defaults to off.
    protected hardwareOnlyDimensions(): boolean {
        return false;
    }

    // whether to draw the frame anchors while this view is up. Snapshotted and restored around
    // the view. Defaults to off.
    protected showAnchors(): boolean {
        return false;
    }

    // the frame anchor sections this export's drawing measured. Called after buildContent, like
    // glassScheduleRows. Empty = this export reports no anchors.
    protected anchorSections(): RenderAnchorSection[] {
        return [];
    }

    // the frame anchor sections this export measured (see measuredAnchors).
    anchors(): RenderAnchorSection[] {
        return this.measuredAnchors;
    }

    // the lites this export's drawing measured. Called after buildContent, so a subclass can
    // read its built views. Empty = this export measures no glass.
    protected glassScheduleRows(): GlassScheduleRow[] {
        return [];
    }

    // the lites this export measured (see measuredGlass).
    glassSchedule(): GlassScheduleRow[] {
        return this.measuredGlass;
    }

    // the frame sticks this export's drawing measured. Called after buildContent, like
    // glassScheduleRows. Empty = this export reports no cut plan.
    protected cutPlanSticks(): CutPlanStick[] {
        return [];
    }

    // the frame cut plan this export measured (see measuredCutPlan).
    cutPlan(): CutPlanStick[] {
        return this.measuredCutPlan;
    }

    // the frame's outside-to-outside size, in drawing units. Read after layout has settled.
    // null = this export draws no frame.
    outsideSize(): {width: number; height: number} | null {
        return null;
    }

    setup(app: DrawFrmApp) {
        super.setup(app);
        this.restoreStyle = applyExportStyle(app);

        // scene-scoped view settings (restored on dispose). Set before buildContent
        // so the drawing initializes with the toggles already applied.
        const vs = this.elev.viewSettings;
        const prevDimShowAll = vs.dimShowAll.value;
        const prevHideHwLabels = vs.hideHardwareLabels.value;
        const prevExternalDims = vs.externalDimensions.value;
        const prevHardwareOnlyDims = vs.hardwareOnlyDimensions.value;
        const prevShowLiteMarks = vs.showLiteMarks.value;
        const prevShowAnchors = vs.showAnchors.value;
        vs.showLiteMarks.emit(true);
        vs.showAnchors.emit(this.showAnchors());
        if(this.showAllDimensions()) vs.dimShowAll.emit(true);
        if(this.hideHardwareLabels()) vs.hideHardwareLabels.emit(true);
        if(this.externalDimensions()) vs.externalDimensions.emit(true);
        if(this.hardwareOnlyDimensions()) vs.hardwareOnlyDimensions.emit(true);
        this.restoreViewSettings = () => {
            vs.dimShowAll.emit(prevDimShowAll);
            vs.hideHardwareLabels.emit(prevHideHwLabels);
            vs.externalDimensions.emit(prevExternalDims);
            vs.hardwareOnlyDimensions.emit(prevHardwareOnlyDims);
            vs.showLiteMarks.emit(prevShowLiteMarks);
            vs.showAnchors.emit(prevShowAnchors);
        };

        const content = this.buildAndLayout();

        // dimension lines are stroked in scene units, so the fit-to-sheet shrink
        // (minAppliedScale) can push them below a pixel and drop them from the
        // JPEG. Bump the stroke so it renders at EXPORT_DIM_TARGET_PX on the sheet,
        // then rebuild once so the dimensions re-stroke at the new width. Only ever
        // thickens (never thins a drawing that already fits at natural scale).
        const scale = content.minAppliedScale();
        if(isFinite(scale) && scale > 0) {
            const want = EXPORT_DIM_TARGET_PX / scale;
            if(want > app.dimensionStyle.strokeWidth) {
                app.dimensionStyle = {...app.dimensionStyle, strokeWidth: want};
                this.resetContent();
                this.buildAndLayout();
            }
        }
    }

    // build the drawing content and lay it out into the export sheet. Returns the
    // content node so the caller can read its post-layout applied scale.
    private buildAndLayout(): FrmSceneLayout {
        const content = this.buildContent();

        // measure the lites for the glazing submittal to report; the schedule is not drawn on
        // the sheet — the submittal prints it as a table of its own.
        this.measuredGlass = this.glassScheduleRows();
        this.measuredCutPlan = this.cutPlanSticks();
        this.measuredAnchors = this.anchorSections();

        this.setupLayout(
            FrmSceneLayout.column(content.grow(1))
                .grow(1)
                .padding(40),
        );

        return content;
    }

    dispose() {
        this.restoreStyle?.();
        this.restoreStyle = null;
        this.restoreViewSettings?.();
        this.restoreViewSettings = null;
        super.dispose();
    }
}
